import { useEffect, useState } from "react";
import {
  Navigate,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  GoogleAuthProvider,
  getAdditionalUserInfo,
  signInWithEmailAndPassword,
  signInWithPopup,
  setPersistence,
  browserLocalPersistence,
} from "firebase/auth";
import { useIdToken } from "react-firebase-hooks/auth";
import { firebaseAuth } from "../../firebaseConfig";
import { LoginForm } from "@components";
import { useUserSession } from "../../context/UserContext";
import { ServerErrorPage } from "../utils";
import { ValidationError } from "../../types";

const authSuccess: ValidationError = { isError: false };

const isSafeContinuePath = (path: string | null): path is string =>
  Boolean(path && path.startsWith("/") && !path.startsWith("//"));

export const LoginPage = () => {
  const [errors, setErrors] = useState(authSuccess);
  const [isGoogleProcessing, setIsGoogleProcessing] = useState(false);
  const [user, loading, error] = useIdToken(firebaseAuth);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const infoMessage = (location.state as { info?: string } | null)?.info;

  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { createUserSession, setUserSession, persistUserSession } =
    useUserSession();

  useEffect(() => {
    if (user) {
      var userSession = createUserSession(user);
      setUserSession(userSession);
      if (userSession) persistUserSession(userSession);
    }
  }, [user, createUserSession, setUserSession, persistUserSession]);

  if (error) {
    return <ServerErrorPage />;
  }

  if (user) {
    const continuePath = searchParams.get("continue");
    const loadWorkspacePath = isSafeContinuePath(continuePath)
      ? `/load_workspace?continue=${encodeURIComponent(continuePath)}`
      : "/load_workspace";
    return <Navigate to={loadWorkspacePath} />;
  }

  const handleGoogleLogin = async () => {
    setErrors(authSuccess);
    setIsGoogleProcessing(true);

    try {
      await setPersistence(firebaseAuth, browserLocalPersistence);
      const credential = await signInWithPopup(
        firebaseAuth,
        new GoogleAuthProvider(),
      );
      const userSession = createUserSession(credential.user);
      if (userSession) {
        setUserSession(userSession);
        persistUserSession(userSession);
      }
      // Preserve the OAuth continue path (if any) and flag brand-new users
      // so the workspace loader can route them into onboarding.
      const params = new URLSearchParams();
      const continuePath = searchParams.get("continue");
      if (isSafeContinuePath(continuePath)) {
        params.set("continue", continuePath);
      }
      if (getAdditionalUserInfo(credential)?.isNewUser) {
        params.set("from", "signup");
      }
      const query = params.toString();
      navigate(query ? `/load_workspace?${query}` : "/load_workspace");
    } catch (error: any) {
      console.error("[google-login] failed:", error?.code, error?.message);
      if (
        error?.code === "auth/popup-closed-by-user" ||
        error?.code === "auth/cancelled-popup-request"
      ) {
        setErrors(authSuccess);
      } else {
        setErrors({
          isError: true,
          errorFields: [],
          errorMessage: t("errors.google_login_failure"),
        });
      }
    } finally {
      setIsGoogleProcessing(false);
    }
  };

  return (
    <div className="w-screen h-screen">
      <LoginForm
        onClicklogin={(email: string, password: string) => {
          setErrors(authSuccess);
          setPersistence(firebaseAuth, browserLocalPersistence).then(() => {
            signInWithEmailAndPassword(firebaseAuth, email, password).catch(
              (error) => {
                var authResult: ValidationError = {
                  isError: true,
                  errorFields: [],
                  errorMessage: t("errors.login_failure"),
                };
                if (error.code === "auth/invalid-email") {
                  authResult.errorFields = ["email"];
                  authResult.errorMessage = t("errors.email_invalid");
                }
                if (error.code === "auth/wrong-password") {
                  authResult.errorFields = ["password"];
                  authResult.errorMessage = t("errors.wrong_password");
                }

                setErrors(authResult);
              },
            );
          });
        }}
        onClickGoogleLogin={handleGoogleLogin}
        validation={errors}
        infoMessage={infoMessage}
        isProcessing={loading || isGoogleProcessing}
      />
    </div>
  );
};
